import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  RefreshCw,
  UserPlus,
  Award,
  Wallet,
  TrendingUp,
  CalendarPlus,
  AlertTriangle,
  Inbox,
  ChevronLeft,
  ChevronRight,
  KeyRound,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useExecutiveAuthStore } from "../../store/executiveAuthStore";
import { executiveAPI } from "../../services/executiveApi";
import ServerMonitoringTab from "./ServerMonitoringTab";
import { useKioskAutoplay } from "../../hooks/useKioskAutoplay";
import { Tv } from "lucide-react";

const AUTO_REFRESH_SECONDS = 60;
const PAGE_SIZE = 10;

const TREND_RANGE_OPTIONS: { value: string; label: string }[] = [
  { value: "14d", label: "Last 14 Days" },
  { value: "1m", label: "Last 1 Month" },
  { value: "3m", label: "Last 3 Months" },
  { value: "6m", label: "Last 6 Months" },
  { value: "1y", label: "Last 1 Year" },
];

function TrendRangeSelector({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="text-xs font-medium text-neutral-600 border border-neutral-200 rounded-lg px-2 py-1 bg-white hover:border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 transition-colors"
    >
      {TREND_RANGE_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function formatShortDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const STATUS_LABELS: Record<string, string> = {
  pending_payment: "Pending Payment",
  payment_done: "Payment Done",
  training: "In Training",
  assessment_pending: "Assessment Pending",
  passed: "Passed",
  failed: "Failed",
};

const STATUS_COLORS: Record<string, { bg: string; text: string; hex: string }> =
  {
    pending_payment: {
      bg: "bg-neutral-100",
      text: "text-neutral-600",
      hex: "#a3a3a3",
    },
    payment_done: { bg: "bg-sky-100", text: "text-sky-700", hex: "#0284c7" },
    training: { bg: "bg-indigo-100", text: "text-indigo-700", hex: "#4f46e5" },
    assessment_pending: {
      bg: "bg-orange-100",
      text: "text-orange-700",
      hex: "#f97316",
    },
    passed: { bg: "bg-emerald-100", text: "text-emerald-700", hex: "#059669" },
    failed: { bg: "bg-red-100", text: "text-red-700", hex: "#b91c1c" },
  };
const FALLBACK_COLOR = {
  bg: "bg-neutral-100",
  text: "text-neutral-600",
  hex: "#a3a3a3",
};

function inrFull(n: number | null | undefined) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

function inr(n: number | null | undefined) {
  return inrFull(n);
}

function formatINR(n: number | null | undefined) {
  if (n == null) return "—";
  const num = Number(n);
  const abs = Math.abs(num);
  if (abs >= 1_00_00_000) return `₹${(num / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000) return `₹${(num / 1_00_000).toFixed(2)} L`;
  if (abs >= 1_000) return `₹${(num / 1_000).toFixed(1)}K`;
  return `₹${num.toLocaleString("en-IN")}`;
}

function RefreshRing({
  progress,
  spinning,
}: {
  progress: number;
  spinning: boolean;
}) {
  const r = 9;
  const c = 2 * Math.PI * r;
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className="shrink-0">
      <circle
        cx="12"
        cy="12"
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.25)"
        strokeWidth="2"
      />
      <circle
        cx="12"
        cy="12"
        r={r}
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - progress)}
        strokeLinecap="round"
        transform="rotate(-90 12 12)"
        className={
          spinning
            ? ""
            : "transition-[stroke-dashoffset] duration-1000 ease-linear"
        }
      />
    </svg>
  );
}

function KpiCard({
  label,
  value,
  icon,
  accent,
  iconBg,
  loading,
  tillDate,
  exactValue,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  accent: string;
  iconBg: string;
  loading: boolean;
  tillDate?: boolean;
  exactValue?: string;
}) {
  return (
    <div className="relative bg-white rounded-2xl shadow-sm border border-neutral-200 p-5 overflow-hidden group hover:shadow-md transition-shadow">
      <div className={`absolute top-0 left-0 right-0 h-1 ${accent}`} />
      <div className="flex items-start justify-between mb-4">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`}
        >
          {icon}
        </div>
        {tillDate && (
          <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wide bg-neutral-100 px-2 py-0.5 rounded-full">
            <b>TILL DATE</b>
          </span>
        )}
      </div>
      <p className="text-neutral-500 text-xs font-medium uppercase tracking-wide mb-1.5">
        {label}
      </p>
      {loading ? (
        <div className="h-8 w-24 bg-neutral-100 rounded-md animate-pulse" />
      ) : (
        <p
          className="text-2xl font-bold text-secondary tabular-nums"
          title={exactValue}
        >
          {value}
        </p>
      )}
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
  isEmpty,
  loading,
  headerRight,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  isEmpty: boolean;
  loading: boolean;
  headerRight?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-secondary mb-1">{title}</h2>
          {subtitle && <p className="text-xs text-neutral-400">{subtitle}</p>}
        </div>
        {headerRight}
      </div>
      <div className="mt-4">
        {loading ? (
          <div className="h-56 bg-neutral-100 rounded-lg animate-pulse" />
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-56 flex flex-col items-center justify-center text-center text-neutral-400 gap-2">
      <Inbox className="w-8 h-8" strokeWidth={1.5} />
      <p className="text-sm">
        Nothing here yet — new activity will appear automatically.
      </p>
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
  totalItems,
  pageSize,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
  totalItems: number;
  pageSize: number;
}) {
  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  const pages = useMemo(() => {
    const arr: (number | "ellipsis")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) arr.push(i);
    } else {
      arr.push(1);
      if (page > 3) arr.push("ellipsis");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      )
        arr.push(i);
      if (page < totalPages - 2) arr.push("ellipsis");
      arr.push(totalPages);
    }
    return arr;
  }, [page, totalPages]);

  if (totalItems === 0) return null;

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 mt-4 pt-4 border-t border-neutral-100">
      <p className="text-xs text-neutral-400">
        Showing {start}–{end} of {totalItems.toLocaleString("en-IN")}
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          className="flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-lg text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Prev</span>
        </button>
        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <span key={`e${i}`} className="px-2 text-neutral-400 text-sm">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={`min-w-[32px] px-2 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                p === page
                  ? "bg-secondary text-white"
                  : "text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              {p}
            </button>
          ),
        )}
        <button
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          className="flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-lg text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const { user, clearAuth } = useExecutiveAuthStore();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SECONDS);
  const [activeTab, setActiveTab] = useState<"business" | "monitoring">(
    "business",
  );
  const [page, setPage] = useState(1);
  const [trendRange, setTrendRange] = useState("14d");
  const [kioskMode, setKioskMode] = useState(true);
  const mainRef = useRef<HTMLElement>(null);

  const handleCycleEnd = useCallback(() => {
    setActiveTab((t) => (t === "business" ? "monitoring" : "business"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useKioskAutoplay(mainRef, kioskMode, 6000, handleCycleEnd);

  const fetchData = useCallback(
    async (isManual = false, pageArg?: number, trendRangeArg?: string) => {
      const targetPage = isManual ? 1 : (pageArg ?? page);
      const targetTrendRange = trendRangeArg ?? trendRange;
      setLoading((prev) => prev || isManual);
      setError("");
      try {
        const res = await executiveAPI.getDashboard({
          page: targetPage,
          page_size: PAGE_SIZE,
          trend_range: targetTrendRange,
        });
        setData(res.data);
        setLastRefresh(new Date());
        setCountdown(AUTO_REFRESH_SECONDS);
        if (isManual) setPage(1);
      } catch (err: any) {
        setError(
          err.response?.data?.detail ||
            "Failed to load dashboard data. Retrying shortly…",
        );
      } finally {
        setLoading(false);
      }
    },
    [page, trendRange],
  );

  useEffect(() => {
    fetchData(true);
  }, []);
  useEffect(() => {
    fetchData(false, page);
  }, [page]);
  // Trend range changes only need to re-fetch charts, but this endpoint
  // returns everything together — reusing the existing API as instructed,
  // rather than adding a second endpoint. Page is passed through unchanged
  // so switching ranges doesn't reset which page of Recent Enrollments
  // the user is looking at.
  useEffect(() => {
    fetchData(false, page, trendRange);
  }, [trendRange]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchData();
          return AUTO_REFRESH_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleLogout = () => {
    clearAuth();
    navigate("/executive/login");
  };

  const statusData = useMemo(
    () =>
      (data?.charts?.enrollment_by_status || []).map((row: any) => ({
        ...row,
        color: (STATUS_COLORS[row.status] || FALLBACK_COLOR).hex,
        label: STATUS_LABELS[row.status] || row.status,
      })),
    [data],
  );
  const totalStatusCount = statusData.reduce(
    (sum: number, r: any) => sum + r.count,
    0,
  );
// Keep this for future reference — the program chart is currently disabled, but this is how it would be calculated if it were re-enabled
  // const programData = useMemo(
  //   () => (data?.charts?.enrollment_by_program || []).slice().reverse(),
  //   [data],
  // );
  // Only this program is shown in the "Enrollments by Program" card
const SHOWN_PROGRAM = "Basic Road Safety & First Aid";
const normalizeProgram = (s: string) =>
  (s || "").toLowerCase().replace(/\s+/g, " ").replace(/\s*&\s*/g, " & ").trim();

const programData = useMemo(
  () =>
    (data?.charts?.enrollment_by_program || []).filter(
      (row: any) => normalizeProgram(row.program) === normalizeProgram(SHOWN_PROGRAM),
    ),
  [data],
);
  const enrollments = data?.recent_enrollments || [];
  const pagination = data?.recent_enrollments_pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const totalItems = pagination?.total ?? enrollments.length;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const cards = [
    {
      label: "Total Enrollments",
      value: data?.cards?.total_enrollments?.toLocaleString("en-IN") ?? "—",
      icon: <UserPlus className="w-5 h-5 text-blue-600" />,
      accent: "bg-blue-500",
      iconBg: "bg-blue-500/10",
      tillDate: true,
    },
    {
      label: "Certificates Issued",
      value: data?.cards?.certificates_issued?.toLocaleString("en-IN") ?? "—",
      icon: <Award className="w-5 h-5 text-purple-600" />,
      accent: "bg-purple-500",
      iconBg: "bg-purple-500/10",
      tillDate: true,
    },
    {
      label: "Total Payment",
      value: formatINR(data?.cards?.total_payment),
      icon: <Wallet className="w-5 h-5 text-emerald-600" />,
      accent: "bg-emerald-500",
      iconBg: "bg-emerald-500/10",
      tillDate: true,
      exactValue: inrFull(data?.cards?.total_payment),
    },
    {
      label: "Enrollments Today",
      value: data?.cards?.enrollments_today?.toLocaleString("en-IN") ?? "—",
      icon: <CalendarPlus className="w-5 h-5 text-indigo-600" />,
      accent: "bg-indigo-500",
      iconBg: "bg-indigo-500/10",
    },
    {
      label: "Today's Revenue",
      value: inrFull(data?.cards?.revenue_today),
      icon: <TrendingUp className="w-5 h-5 text-primary" />,
      accent: "bg-primary",
      iconBg: "bg-primary/10",
      exactValue: inrFull(data?.cards?.revenue_today),
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="h-1 bg-primary" />

      <header className="bg-secondary text-white px-6 py-4 flex items-center justify-between shadow-lg sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src="/logo.png"
            alt="IRCS"
            className="w-9 h-9 object-contain bg-white rounded-full p-1 shrink-0"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg truncate">
                Haryana Red Cross Project
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-primary/90 px-2 py-0.5 rounded-full shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                LIVE
              </span>
            </div>
            <span className="text-secondary-light text-xs">
              Executive Dashboard · Read-only
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 text-sm shrink-0">
          <span className="text-blue-200 hidden md:block">
            Welcome, {user?.full_name}
          </span>
          <button
            onClick={() => fetchData(true)}
            disabled={loading}
            title="Refresh now"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
            <RefreshRing
              progress={countdown / AUTO_REFRESH_SECONDS}
              spinning={loading}
            />
          </button>
          <button
            onClick={() => setKioskMode((v) => !v)}
            title={
              kioskMode
                ? "Auto Play is on — the screen scrolls through every chart on its own, then switches between Business Overview and Server Monitoring. Click to pause."
                : "Auto Play is off. Click to let the screen scroll through every chart automatically — useful for a TV or unattended monitor."
            }
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${
              kioskMode
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-white/10 hover:bg-white/20"
            }`}
          >
            <Tv className="w-4 h-4" />
            <span className="hidden sm:inline">
              {kioskMode ? "Auto Play: On" : "Auto Play: Off"}
            </span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark px-3 py-1.5 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
          <button
            onClick={() => navigate("/executive/change-password")}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors"
          >
            <KeyRound className="w-4 h-4" />
            <span className="hidden sm:inline">Change Password</span>
          </button>
        </div>
      </header>

      <main ref={mainRef} className="p-6 md:p-8 max-w-7xl mx-auto">
        {kioskMode && (
          <p className="text-xs text-neutral-500 mb-2 flex items-center gap-1.5">
            <Tv className="w-3.5 h-3.5" />
            Auto Play is on — this screen will scroll through each chart on its
            own and switch tabs automatically. Click "Auto Play" above to take
            manual control.
          </p>
        )}
        <div className="inline-flex items-center bg-white border border-neutral-200 rounded-xl p-1 gap-0.5 mb-5 shadow-sm">
          <button
            onClick={() => setActiveTab("business")}
            className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
              activeTab === "business"
                ? "bg-secondary text-white"
                : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            Business Overview
          </button>
          <button
            onClick={() => setActiveTab("monitoring")}
            className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
              activeTab === "monitoring"
                ? "bg-secondary text-white"
                : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            Server Monitoring
          </button>
        </div>

        {activeTab === "monitoring" && <ServerMonitoringTab />}

        {activeTab === "business" && (
          <>
            <div className="flex items-center justify-between mb-5">
              <p className="text-xs text-neutral-600 font-medium">
                {lastRefresh ? (
                  <>
                    Last updated {lastRefresh.toLocaleTimeString("en-IN")} ·
                    refreshes every {AUTO_REFRESH_SECONDS}s
                  </>
                ) : (
                  "Loading latest data…"
                )}
              </p>
            </div>

            {error && (
              <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {cards.map((c) => (
                <KpiCard key={c.label} {...c} loading={loading && !data} />
              ))}
            </div>

            <div
              data-kiosk-section
              style={{ overflowAnchor: "none" }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
            >
              <ChartCard
                title="Enrollments by Status"
                loading={loading && !data}
                isEmpty={!statusData.length}
              >
                <div className="flex items-center gap-6">
                  <ResponsiveContainer width="45%" height={200}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        dataKey="count"
                        nameKey="label"
                        innerRadius={55}
                        outerRadius={82}
                        paddingAngle={2}
                        strokeWidth={0}
                      >
                        {statusData.map((entry: any, i: number) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => [v, "Enrollments"]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2.5">
                    <p className="text-xs text-neutral-400 mb-1">
                      {totalStatusCount.toLocaleString("en-IN")} total
                    </p>
                    {statusData.map((row: any) => (
                      <div
                        key={row.status}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: row.color }}
                          />
                          <span className="text-sm text-neutral-600 truncate">
                            {row.label}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-neutral-800 tabular-nums">
                          {row.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </ChartCard>

              <ChartCard
                title="Enrollments by Program"
                loading={loading && !data}
                isEmpty={!programData.length}
              >
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={programData}
                    layout="vertical"
                    margin={{ left: 8, right: 16 }}
                  >
                    <CartesianGrid horizontal={false} stroke="#f0f0f0" />
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="program"
                      width={130}
                      tick={{ fontSize: 12, fill: "#525252" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(v: number) => [v, "Enrollments"]}
                      cursor={{ fill: "#fafafa" }}
                    />
                    <Bar
                      dataKey="count"
                      fill="rgb(7 39 89)"
                      radius={[0, 6, 6, 0]}
                      barSize={16}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <div
              data-kiosk-section
              style={{ overflowAnchor: "none" }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
            >
              <ChartCard
                title="Enrollment Trend"
                subtitle={
                  <span className="text-gray-700 font-medium">
                    {data?.trend_meta?.clamped_to_system_start
                      ? `Showing data from ${formatShortDate(data.trend_meta.effective_start_date)} to Today (System Start: ${formatShortDate(data.trend_meta.system_start_date)})`
                      : data?.trend_meta
                        ? `${formatShortDate(data.trend_meta.effective_start_date)} – Today`
                        : ""}
                  </span>
                }
                loading={loading && !data}
                isEmpty={!data?.charts?.enrollment_trend?.length}
                headerRight={
                  <TrendRangeSelector
                    value={trendRange}
                    onChange={setTrendRange}
                    disabled={loading}
                  />
                }
              >
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart
                    data={data?.charts?.enrollment_trend || []}
                    margin={{ left: -20, right: 8 }}
                  >
                    <defs>
                      <linearGradient
                        id="enrollGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="rgb(185 28 28)"
                          stopOpacity={0.35}
                        />
                        <stop
                          offset="100%"
                          stopColor="rgb(185 28 28)"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "#a3a3a3" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(d: string) => d.slice(5)}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#a3a3a3" }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      formatter={(v: number) => [v, "Enrollments"]}
                      labelFormatter={(l) => `Date: ${l}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="rgb(185 28 28)"
                      strokeWidth={2}
                      fill="url(#enrollGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard
                title="Revenue Trend"
                subtitle={
                  <span className="text-gray-700 font-medium">
                    {data?.trend_meta?.clamped_to_system_start
                      ? `Showing data from ${formatShortDate(data.trend_meta.effective_start_date)} to Today (System Start: ${formatShortDate(data.trend_meta.system_start_date)})`
                      : data?.trend_meta
                        ? `${formatShortDate(data.trend_meta.effective_start_date)} – Today`
                        : ""}
                  </span>
                }
                loading={loading && !data}
                isEmpty={!data?.charts?.revenue_trend?.length}
                headerRight={
                  <TrendRangeSelector
                    value={trendRange}
                    onChange={setTrendRange}
                    disabled={loading}
                  />
                }
              >
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={data?.charts?.revenue_trend || []}
                    margin={{ left: -20, right: 8 }}
                  >
                    <CartesianGrid vertical={false} stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "#a3a3a3" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(d: string) => d.slice(5)}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#a3a3a3" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: number) =>
                        `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`
                      }
                    />
                    <Tooltip
                      formatter={(v: number) => [inr(v), "Revenue"]}
                      labelFormatter={(l) => `Date: ${l}`}
                    />
                    <Bar
                      dataKey="amount"
                      fill="rgb(5 150 105)"
                      radius={[6, 6, 0, 0]}
                      barSize={18}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <div
              data-kiosk-section
              className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-secondary">Recent Enrollments</h2>
                {!!totalItems && (
                  <span className="text-xs text-neutral-400">
                    {totalItems.toLocaleString("en-IN")} total
                  </span>
                )}
              </div>
              {loading && !data ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-10 bg-neutral-100 rounded-lg animate-pulse"
                    />
                  ))}
                </div>
              ) : !enrollments.length ? (
                <EmptyState />
              ) : (
                <>
                  <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm border-separate border-spacing-0">
                      <thead>
                        <tr className="text-left">
                          <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">
                            Applicant
                          </th>
                          <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">
                            Program
                          </th>
                          <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">
                            Enrolled
                          </th>
                          <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">
                            Status
                          </th>
                          <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide text-right">
                            Paid
                          </th>
                          <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide text-center">
                            Certificate
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {enrollments.map((row: any, i: number) => {
                          const sc =
                            STATUS_COLORS[row.status] || FALLBACK_COLOR;
                          const initial = (row.applicant_name || "?")
                            .trim()
                            .charAt(0)
                            .toUpperCase();
                          return (
                            <tr
                              key={row.enrollment_id ?? i}
                              className="hover:bg-neutral-50 transition-colors"
                            >
                              <td className="py-2.5 px-2 border-t border-neutral-100">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-7 h-7 rounded-full bg-secondary/10 text-secondary text-xs font-bold flex items-center justify-center shrink-0">
                                    {initial}
                                  </div>
                                  <span className="text-neutral-800 font-medium truncate max-w-[160px]">
                                    {row.applicant_name}
                                  </span>
                                </div>
                              </td>
                              <td className="py-2.5 px-2 border-t border-neutral-100 text-neutral-600">
                                {row.program_name}
                              </td>
                              <td className="py-2.5 px-2 border-t border-neutral-100 text-neutral-500 whitespace-nowrap">
                                {row.enrolled_at
                                  ? new Date(
                                      row.enrolled_at,
                                    ).toLocaleDateString("en-IN", {
                                      day: "2-digit",
                                      month: "short",
                                    })
                                  : "—"}
                              </td>
                              <td className="py-2.5 px-2 border-t border-neutral-100">
                                <span
                                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.bg} ${sc.text}`}
                                >
                                  {STATUS_LABELS[row.status] || row.status}
                                </span>
                              </td>
                              <td className="py-2.5 px-2 border-t border-neutral-100 text-right text-emerald-700 font-medium whitespace-nowrap">
                                {inr(row.amount_paid)}
                              </td>
                              <td className="py-2.5 px-2 border-t border-neutral-100 text-center">
                                {row.certificate_issued ? (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                                    Issued
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500 font-medium">
                                    Pending
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onChange={setPage}
                    totalItems={totalItems}
                    pageSize={PAGE_SIZE}
                  />
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
// import { useNavigate } from 'react-router-dom'
// import {
//   LogOut, RefreshCw, UserPlus, Award,
//   Wallet, TrendingUp, CalendarPlus, AlertTriangle, Inbox,
//   ChevronLeft, ChevronRight, KeyRound,
// } from 'lucide-react'
// import {
//   AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
//   XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
// } from 'recharts'
// import { useExecutiveAuthStore } from '../../store/executiveAuthStore'
// import { executiveAPI } from '../../services/executiveApi'
// import ServerMonitoringTab from './ServerMonitoringTab'
// import { useKioskAutoplay } from '../../hooks/useKioskAutoplay'
// import { Tv } from 'lucide-react'

// const AUTO_REFRESH_SECONDS = 60
// const PAGE_SIZE = 10

// const STATUS_LABELS: Record<string, string> = {
//   pending_payment: 'Pending Payment',
//   payment_done: 'Payment Done',
//   training: 'In Training',
//   assessment_pending: 'Assessment Pending',
//   passed: 'Passed',
//   failed: 'Failed',
// }

// const STATUS_COLORS: Record<string, { bg: string; text: string; hex: string }> = {
//   pending_payment: { bg: 'bg-neutral-100', text: 'text-neutral-600', hex: '#a3a3a3' },
//   payment_done: { bg: 'bg-sky-100', text: 'text-sky-700', hex: '#0284c7' },
//   training: { bg: 'bg-indigo-100', text: 'text-indigo-700', hex: '#4f46e5' },
//   assessment_pending: { bg: 'bg-orange-100', text: 'text-orange-700', hex: '#f97316' },
//   passed: { bg: 'bg-emerald-100', text: 'text-emerald-700', hex: '#059669' },
//   failed: { bg: 'bg-red-100', text: 'text-red-700', hex: '#b91c1c' },
// }
// const FALLBACK_COLOR = { bg: 'bg-neutral-100', text: 'text-neutral-600', hex: '#a3a3a3' }

// function inrFull(n: number | null | undefined) {
//   if (n == null) return '—'
//   return `₹${Number(n).toLocaleString('en-IN')}`
// }

// function inr(n: number | null | undefined) {
//   return inrFull(n)
// }

// function formatINR(n: number | null | undefined) {
//   if (n == null) return '—'
//   const num = Number(n)
//   const abs = Math.abs(num)
//   if (abs >= 1_00_00_000) return `₹${(num / 1_00_00_000).toFixed(2)} Cr`
//   if (abs >= 1_00_000) return `₹${(num / 1_00_000).toFixed(2)} L`
//   if (abs >= 1_000) return `₹${(num / 1_000).toFixed(1)}K`
//   return `₹${num.toLocaleString('en-IN')}`
// }

// function RefreshRing({ progress, spinning }: { progress: number; spinning: boolean }) {
//   const r = 9
//   const c = 2 * Math.PI * r
//   return (
//     <svg width="24" height="24" viewBox="0 0 24 24" className="shrink-0">
//       <circle cx="12" cy="12" r={r} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
//       <circle
//         cx="12" cy="12" r={r} fill="none" stroke="white" strokeWidth="2"
//         strokeDasharray={c} strokeDashoffset={c * (1 - progress)}
//         strokeLinecap="round" transform="rotate(-90 12 12)"
//         className={spinning ? '' : 'transition-[stroke-dashoffset] duration-1000 ease-linear'}
//       />
//     </svg>
//   )
// }

// function KpiCard({
//   label, value, icon, accent, iconBg, loading, tillDate, exactValue,
// }: {
//   label: string
//   value: string
//   icon: React.ReactNode
//   accent: string
//   iconBg: string
//   loading: boolean
//   tillDate?: boolean
//   exactValue?: string
// }) {
//   return (
//     <div className="relative bg-white rounded-2xl shadow-sm border border-neutral-200 p-5 overflow-hidden group hover:shadow-md transition-shadow">
//       <div className={`absolute top-0 left-0 right-0 h-1 ${accent}`} />
//       <div className="flex items-start justify-between mb-4">
//         <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`}>
//           {icon}
//         </div>
//         {tillDate && (
//           <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wide bg-neutral-100 px-2 py-0.5 rounded-full">
//             <b>TILL DATE</b>
//           </span>
//         )}
//       </div>
//       <p className="text-neutral-500 text-xs font-medium uppercase tracking-wide mb-1.5">{label}</p>
//       {loading ? (
//         <div className="h-8 w-24 bg-neutral-100 rounded-md animate-pulse" />
//       ) : (
//         <p className="text-2xl font-bold text-secondary tabular-nums" title={exactValue}>
//           {value}
//         </p>
//       )}
//     </div>
//   )
// }

// function ChartCard({ title, children, isEmpty, loading }: {
//   title: string; children: React.ReactNode; isEmpty: boolean; loading: boolean
// }) {
//   return (
//     <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
//       <h2 className="font-bold text-secondary mb-1">{title}</h2>
//       <div className="mt-4">
//         {loading ? (
//           <div className="h-56 bg-neutral-100 rounded-lg animate-pulse" />
//         ) : isEmpty ? (
//           <EmptyState />
//         ) : (
//           children
//         )}
//       </div>
//     </div>
//   )
// }

// function EmptyState() {
//   return (
//     <div className="h-56 flex flex-col items-center justify-center text-center text-neutral-400 gap-2">
//       <Inbox className="w-8 h-8" strokeWidth={1.5} />
//       <p className="text-sm">Nothing here yet — new activity will appear automatically.</p>
//     </div>
//   )
// }

// function Pagination({
//   page, totalPages, onChange, totalItems, pageSize,
// }: {
//   page: number
//   totalPages: number
//   onChange: (p: number) => void
//   totalItems: number
//   pageSize: number
// }) {
//   const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1
//   const end = Math.min(page * pageSize, totalItems)

//   const pages = useMemo(() => {
//     const arr: (number | 'ellipsis')[] = []
//     if (totalPages <= 7) {
//       for (let i = 1; i <= totalPages; i++) arr.push(i)
//     } else {
//       arr.push(1)
//       if (page > 3) arr.push('ellipsis')
//       for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) arr.push(i)
//       if (page < totalPages - 2) arr.push('ellipsis')
//       arr.push(totalPages)
//     }
//     return arr
//   }, [page, totalPages])

//   if (totalItems === 0) return null

//   return (
//     <div className="flex items-center justify-between flex-wrap gap-3 mt-4 pt-4 border-t border-neutral-100">
//       <p className="text-xs text-neutral-400">
//         Showing {start}–{end} of {totalItems.toLocaleString('en-IN')}
//       </p>
//       <div className="flex items-center gap-1">
//         <button
//           onClick={() => onChange(page - 1)}
//           disabled={page === 1}
//           className="flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-lg text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
//         >
//           <ChevronLeft className="w-4 h-4" />
//           <span className="hidden sm:inline">Prev</span>
//         </button>
//         {pages.map((p, i) =>
//           p === 'ellipsis' ? (
//             <span key={`e${i}`} className="px-2 text-neutral-400 text-sm">…</span>
//           ) : (
//             <button
//               key={p}
//               onClick={() => onChange(p)}
//               className={`min-w-[32px] px-2 py-1.5 text-sm rounded-lg font-medium transition-colors ${
//                 p === page ? 'bg-secondary text-white' : 'text-neutral-600 hover:bg-neutral-100'
//               }`}
//             >
//               {p}
//             </button>
//           )
//         )}
//         <button
//           onClick={() => onChange(page + 1)}
//           disabled={page === totalPages}
//           className="flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-lg text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
//         >
//           <span className="hidden sm:inline">Next</span>
//           <ChevronRight className="w-4 h-4" />
//         </button>
//       </div>
//     </div>
//   )
// }

// export default function ExecutiveDashboard() {
//   const navigate = useNavigate()
//   const { user, clearAuth } = useExecutiveAuthStore()
//   const [data, setData] = useState<any>(null)
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState('')
//   const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
//   const [countdown, setCountdown] = useState(AUTO_REFRESH_SECONDS)
//   const [activeTab, setActiveTab] = useState<'business' | 'monitoring'>('business')
//   const [page, setPage] = useState(1)
//   const [kioskMode, setKioskMode] = useState(true)
//   const mainRef = useRef<HTMLElement>(null)

//   const handleCycleEnd = useCallback(() => {
//     setActiveTab((t) => (t === 'business' ? 'monitoring' : 'business'))
//     window.scrollTo({ top: 0, behavior: 'smooth' })
//   }, [])

//   useKioskAutoplay(mainRef, kioskMode, 6000, handleCycleEnd)

//   const fetchData = useCallback(async (isManual = false, pageArg?: number) => {
//     const targetPage = isManual ? 1 : (pageArg ?? page)
//     setLoading((prev) => prev || isManual)
//     setError('')
//     try {
//       const res = await executiveAPI.getDashboard({ page: targetPage, page_size: PAGE_SIZE })
//       setData(res.data)
//       setLastRefresh(new Date())
//       setCountdown(AUTO_REFRESH_SECONDS)
//       if (isManual) setPage(1)
//     } catch (err: any) {
//       setError(err.response?.data?.detail || 'Failed to load dashboard data. Retrying shortly…')
//     } finally {
//       setLoading(false)
//     }
//   }, [page])

//   useEffect(() => { fetchData(true) }, [])
//   useEffect(() => { fetchData(false, page) }, [page])

//   useEffect(() => {
//     const interval = setInterval(() => {
//       setCountdown((prev) => {
//         if (prev <= 1) {
//           fetchData()
//           return AUTO_REFRESH_SECONDS
//         }
//         return prev - 1
//       })
//     }, 1000)
//     return () => clearInterval(interval)
//   }, [fetchData])

//   const handleLogout = () => {
//     clearAuth()
//     navigate('/executive/login')
//   }

//   const statusData = useMemo(
//     () => (data?.charts?.enrollment_by_status || []).map((row: any) => ({
//       ...row,
//       color: (STATUS_COLORS[row.status] || FALLBACK_COLOR).hex,
//       label: STATUS_LABELS[row.status] || row.status,
//     })),
//     [data]
//   )
//   const totalStatusCount = statusData.reduce((sum: number, r: any) => sum + r.count, 0)

//   const programData = useMemo(
//     () => (data?.charts?.enrollment_by_program || []).slice().reverse(),
//     [data]
//   )

//   const enrollments = data?.recent_enrollments || []
//   const pagination = data?.recent_enrollments_pagination
//   const totalPages = pagination?.total_pages ?? 1
//   const totalItems = pagination?.total ?? enrollments.length

//   useEffect(() => {
//     if (page > totalPages) setPage(totalPages)
//   }, [totalPages, page])

//   const cards = [
//     {
//       label: 'Total Enrollments',
//       value: data?.cards?.total_enrollments?.toLocaleString('en-IN') ?? '—',
//       icon: <UserPlus className="w-5 h-5 text-blue-600" />,
//       accent: 'bg-blue-500',
//       iconBg: 'bg-blue-500/10',
//       tillDate: true,
//     },
//     {
//       label: 'Certificates Issued',
//       value: data?.cards?.certificates_issued?.toLocaleString('en-IN') ?? '—',
//       icon: <Award className="w-5 h-5 text-purple-600" />,
//       accent: 'bg-purple-500',
//       iconBg: 'bg-purple-500/10',
//       tillDate: true,
//     },
//     {
//       label: 'Total Payment',
//       value: formatINR(data?.cards?.total_payment),
//       icon: <Wallet className="w-5 h-5 text-emerald-600" />,
//       accent: 'bg-emerald-500',
//       iconBg: 'bg-emerald-500/10',
//       tillDate: true,
//       exactValue: inrFull(data?.cards?.total_payment),
//     },
//     {
//       label: 'Enrollments Today',
//       value: data?.cards?.enrollments_today?.toLocaleString('en-IN') ?? '—',
//       icon: <CalendarPlus className="w-5 h-5 text-indigo-600" />,
//       accent: 'bg-indigo-500',
//       iconBg: 'bg-indigo-500/10',
//     },
//     {
//       label: "Today's Revenue",
//       value: inrFull(data?.cards?.revenue_today),
//       icon: <TrendingUp className="w-5 h-5 text-primary" />,
//       accent: 'bg-primary',
//       iconBg: 'bg-primary/10',
//       exactValue: inrFull(data?.cards?.revenue_today),
//     },
//   ]

//   return (
//     <div className="min-h-screen bg-neutral-50">
//       <div className="h-1 bg-primary" />

//       <header className="bg-secondary text-white px-6 py-4 flex items-center justify-between shadow-lg sticky top-0 z-10">
//         <div className="flex items-center gap-3 min-w-0">
//           <img
//             src="/logo.png"
//             alt="IRCS"
//             className="w-9 h-9 object-contain bg-white rounded-full p-1 shrink-0"
//           />
//           <div className="min-w-0">
//             <div className="flex items-center gap-2">
//               <span className="font-bold text-lg truncate">Haryana Red Cross Project</span>
//               <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-primary/90 px-2 py-0.5 rounded-full shrink-0">
//                 <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
//                 LIVE
//               </span>
//             </div>
//             <span className="text-secondary-light text-xs">Executive Dashboard · Read-only</span>
//           </div>
//         </div>

//         <div className="flex items-center gap-3 text-sm shrink-0">
//           <span className="text-blue-200 hidden md:block">
//             Welcome, {user?.full_name}
//           </span>
//           <button
//             onClick={() => fetchData(true)}
//             disabled={loading}
//             title="Refresh now"
//             className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
//             <span className="hidden sm:inline">Refresh</span>
//             <RefreshRing progress={countdown / AUTO_REFRESH_SECONDS} spinning={loading} />
//           </button>
//           <button
//             onClick={() => setKioskMode((v) => !v)}
//             title={
//               kioskMode
//                 ? 'Auto Play is on — the screen scrolls through every chart on its own, then switches between Business Overview and Server Monitoring. Click to pause.'
//                 : 'Auto Play is off. Click to let the screen scroll through every chart automatically — useful for a TV or unattended monitor.'
//             }
//             className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${
//               kioskMode ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-white/10 hover:bg-white/20'
//             }`}
//           >
//             <Tv className="w-4 h-4" />
//             <span className="hidden sm:inline">{kioskMode ? 'Auto Play: On' : 'Auto Play: Off'}</span>
//           </button>
//           <button
//             onClick={handleLogout}
//             className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark px-3 py-1.5 rounded-lg transition-colors"
//           >
//             <LogOut className="w-4 h-4" />
//             <span className="hidden sm:inline">Logout</span>
//           </button>
//           <button
//             onClick={() => navigate('/executive/change-password')}
//             className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors"
//           >
//             <KeyRound className="w-4 h-4" />
//             <span className="hidden sm:inline">Change Password</span>
//           </button>
//         </div>
//       </header>

//       <main ref={mainRef} className="p-6 md:p-8 max-w-7xl mx-auto">
//         {kioskMode && (
//           <p className="text-xs text-neutral-500 mb-2 flex items-center gap-1.5">
//             <Tv className="w-3.5 h-3.5" />
//             Auto Play is on — this screen will scroll through each chart on its own and switch tabs automatically. Click "Auto Play" above to take manual control.
//           </p>
//         )}
//         <div className="inline-flex items-center bg-white border border-neutral-200 rounded-xl p-1 gap-0.5 mb-5 shadow-sm">
//           <button
//             onClick={() => setActiveTab('business')}
//             className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
//               activeTab === 'business' ? 'bg-secondary text-white' : 'text-neutral-500 hover:text-neutral-700'
//             }`}
//           >
//             Business Overview
//           </button>
//           <button
//             onClick={() => setActiveTab('monitoring')}
//             className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
//               activeTab === 'monitoring' ? 'bg-secondary text-white' : 'text-neutral-500 hover:text-neutral-700'
//             }`}
//           >
//             Server Monitoring
//           </button>
//         </div>

//         {activeTab === 'monitoring' && <ServerMonitoringTab />}

//         {activeTab === 'business' && (
//         <>
//         <div className="flex items-center justify-between mb-5">
//           <p className="text-xs text-neutral-600 font-medium">
//             {lastRefresh
//               ? <>Last updated {lastRefresh.toLocaleTimeString('en-IN')} · refreshes every {AUTO_REFRESH_SECONDS}s</>
//               : 'Loading latest data…'}
//           </p>
//         </div>

//         {error && (
//           <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
//             <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
//             <span className="text-sm">{error}</span>
//           </div>
//         )}

//         <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
//           {cards.map((c) => (
//             <KpiCard key={c.label} {...c} loading={loading && !data} />
//           ))}
//         </div>

//         <div data-kiosk-section style={{ overflowAnchor: 'none' }} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
//           <ChartCard title="Enrollments by Status" loading={loading && !data} isEmpty={!statusData.length}>
//             <div className="flex items-center gap-6">
//               <ResponsiveContainer width="45%" height={200}>
//                 <PieChart>
//                   <Pie
//                     data={statusData} dataKey="count" nameKey="label"
//                     innerRadius={55} outerRadius={82} paddingAngle={2} strokeWidth={0}
//                   >
//                     {statusData.map((entry: any, i: number) => (
//                       <Cell key={i} fill={entry.color} />
//                     ))}
//                   </Pie>
//                   <Tooltip formatter={(v: number) => [v, 'Enrollments']} />
//                 </PieChart>
//               </ResponsiveContainer>
//               <div className="flex-1 space-y-2.5">
//                 <p className="text-xs text-neutral-400 mb-1">{totalStatusCount.toLocaleString('en-IN')} total</p>
//                 {statusData.map((row: any) => (
//                   <div key={row.status} className="flex items-center justify-between">
//                     <div className="flex items-center gap-2 min-w-0">
//                       <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: row.color }} />
//                       <span className="text-sm text-neutral-600 truncate">{row.label}</span>
//                     </div>
//                     <span className="text-sm font-bold text-neutral-800 tabular-nums">{row.count}</span>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </ChartCard>

//           <ChartCard title="Enrollments by Program" loading={loading && !data} isEmpty={!programData.length}>
//             <ResponsiveContainer width="100%" height={220}>
//               <BarChart data={programData} layout="vertical" margin={{ left: 8, right: 16 }}>
//                 <CartesianGrid horizontal={false} stroke="#f0f0f0" />
//                 <XAxis type="number" hide />
//                 <YAxis
//                   type="category" dataKey="program" width={130}
//                   tick={{ fontSize: 12, fill: '#525252' }} axisLine={false} tickLine={false}
//                 />
//                 <Tooltip formatter={(v: number) => [v, 'Enrollments']} cursor={{ fill: '#fafafa' }} />
//                 <Bar dataKey="count" fill="rgb(7 39 89)" radius={[0, 6, 6, 0]} barSize={16} />
//               </BarChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </div>

//         <div data-kiosk-section style={{ overflowAnchor: 'none' }} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
//           <ChartCard
//             title="Enrollment Trend — Last 14 Days"
//             loading={loading && !data}
//             isEmpty={!data?.charts?.enrollment_trend_14d?.length}
//           >
//             <ResponsiveContainer width="100%" height={220}>
//               <AreaChart data={data?.charts?.enrollment_trend_14d || []} margin={{ left: -20, right: 8 }}>
//                 <defs>
//                   <linearGradient id="enrollGradient" x1="0" y1="0" x2="0" y2="1">
//                     <stop offset="0%" stopColor="rgb(185 28 28)" stopOpacity={0.35} />
//                     <stop offset="100%" stopColor="rgb(185 28 28)" stopOpacity={0} />
//                   </linearGradient>
//                 </defs>
//                 <CartesianGrid vertical={false} stroke="#f0f0f0" />
//                 <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
//                   tickFormatter={(d: string) => d.slice(5)} />
//                 <YAxis tick={{ fontSize: 11, fill: '#a3a3a3' }} axisLine={false} tickLine={false} allowDecimals={false} />
//                 <Tooltip formatter={(v: number) => [v, 'Enrollments']} labelFormatter={(l) => `Date: ${l}`} />
//                 <Area type="monotone" dataKey="count" stroke="rgb(185 28 28)" strokeWidth={2} fill="url(#enrollGradient)" />
//               </AreaChart>
//             </ResponsiveContainer>
//           </ChartCard>

//           <ChartCard
//             title="Revenue Trend — Last 14 Days"
//             loading={loading && !data}
//             isEmpty={!data?.charts?.revenue_trend_14d?.length}
//           >
//             <ResponsiveContainer width="100%" height={220}>
//               <BarChart data={data?.charts?.revenue_trend_14d || []} margin={{ left: -20, right: 8 }}>
//                 <CartesianGrid vertical={false} stroke="#f0f0f0" />
//                 <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
//                   tickFormatter={(d: string) => d.slice(5)} />
//                 <YAxis tick={{ fontSize: 11, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
//                   tickFormatter={(v: number) => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
//                 <Tooltip formatter={(v: number) => [inr(v), 'Revenue']} labelFormatter={(l) => `Date: ${l}`} />
//                 <Bar dataKey="amount" fill="rgb(5 150 105)" radius={[6, 6, 0, 0]} barSize={18} />
//               </BarChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </div>

//         <div data-kiosk-section className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
//           <div className="flex items-center justify-between mb-4">
//             <h2 className="font-bold text-secondary">Recent Enrollments</h2>
//             {!!totalItems && (
//               <span className="text-xs text-neutral-400">
//                 {totalItems.toLocaleString('en-IN')} total
//               </span>
//             )}
//           </div>
//           {loading && !data ? (
//             <div className="space-y-3">
//               {Array.from({ length: 5 }).map((_, i) => (
//                 <div key={i} className="h-10 bg-neutral-100 rounded-lg animate-pulse" />
//               ))}
//             </div>
//           ) : !enrollments.length ? (
//             <EmptyState />
//           ) : (
//             <>
//               <div className="overflow-x-auto -mx-2">
//                 <table className="w-full text-sm border-separate border-spacing-0">
//                   <thead>
//                     <tr className="text-left">
//                       <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">Applicant</th>
//                       <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">Program</th>
//                       <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">Enrolled</th>
//                       <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">Status</th>
//                       <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide text-right">Paid</th>
//                       <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide text-center">Certificate</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {enrollments.map((row: any, i: number) => {
//                       const sc = STATUS_COLORS[row.status] || FALLBACK_COLOR
//                       const initial = (row.applicant_name || '?').trim().charAt(0).toUpperCase()
//                       return (
//                         <tr key={row.enrollment_id ?? i} className="hover:bg-neutral-50 transition-colors">
//                           <td className="py-2.5 px-2 border-t border-neutral-100">
//                             <div className="flex items-center gap-2.5">
//                               <div className="w-7 h-7 rounded-full bg-secondary/10 text-secondary text-xs font-bold flex items-center justify-center shrink-0">
//                                 {initial}
//                               </div>
//                               <span className="text-neutral-800 font-medium truncate max-w-[160px]">{row.applicant_name}</span>
//                             </div>
//                           </td>
//                           <td className="py-2.5 px-2 border-t border-neutral-100 text-neutral-600">{row.program_name}</td>
//                           <td className="py-2.5 px-2 border-t border-neutral-100 text-neutral-500 whitespace-nowrap">
//                             {row.enrolled_at ? new Date(row.enrolled_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
//                           </td>
//                           <td className="py-2.5 px-2 border-t border-neutral-100">
//                             <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.bg} ${sc.text}`}>
//                               {STATUS_LABELS[row.status] || row.status}
//                             </span>
//                           </td>
//                           <td className="py-2.5 px-2 border-t border-neutral-100 text-right text-emerald-700 font-medium whitespace-nowrap">
//                             {inr(row.amount_paid)}
//                           </td>
//                           <td className="py-2.5 px-2 border-t border-neutral-100 text-center">
//                             {row.certificate_issued ? (
//                               <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Issued</span>
//                             ) : (
//                               <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500 font-medium">Pending</span>
//                             )}
//                           </td>
//                         </tr>
//                       )
//                     })}
//                   </tbody>
//                 </table>
//               </div>

//               <Pagination
//                 page={page}
//                 totalPages={totalPages}
//                 onChange={setPage}
//                 totalItems={totalItems}
//                 pageSize={PAGE_SIZE}
//               />
//             </>
//           )}
//         </div>
//         </>
//         )}
//       </main>
//     </div>
//   )
// }
